/* eslint github/unescaped-html-literal: off */
import type {StylingDirectivesLine} from '@github-ui/code-view-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {showHiddenUnicodeCharactersHTML} from '@github-ui/hidden-unicode-banner/HiddenUnicodeCharacter'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useCodeHTML} from '@github-ui/syntax-highlighted-lines/hooks/use-code-html'
import type {HighlightingStrategy} from '@github-ui/syntax-highlighted-lines/types'
import React, {useMemo} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import {useIsFirefox, useShouldUseInert} from '../../../../utilities/lines'
import {isHiddenUnicodeShown} from '../../Banners/HiddenUnicode'
import {numberOfLinesPerContentVisibility} from './CodeLinesNoVirtualization'
import type {CodeLineData} from './hooks/use-code-lines'

interface SyntaxHighlightedLineProps {
  id?: string
  rawText?: string
  stylingDirectivesLine?: StylingDirectivesLine
  lineNumber: number
  current?: boolean
  forceVisible?: boolean
  shouldUseInert: boolean
  colorizedLine?: SafeHTMLString
  ref?: React.Ref<HTMLDivElement>
}

export const SyntaxHighlightedLine = React.memo(SyntaxHighlightedLineWithRef)

function SyntaxHighlightedLineWithRef({
  id,
  stylingDirectivesLine,
  rawText,
  lineNumber,
  current,
  forceVisible,
  shouldUseInert,
  colorizedLine,
  ref,
}: SyntaxHighlightedLineProps) {
  const strategy = useSyntaxHighlightingStrategy()
  const {tabSize} = useCurrentBlob()
  const hiddenUnicodeShown = isHiddenUnicodeShown()
  const codeLineHTML = useCodeHTML(
    colorizedLine,
    stylingDirectivesLine,
    rawText,
    forceVisible ? 'plain' : strategy,
    tabSize,
    hiddenUnicodeShown,
  )

  return (
    <VerifiedHTMLDiv
      id={id}
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className="react-file-line html-div"
      data-testid="code-cell"
      data-line-number={lineNumber}
      html={codeLineHTML}
      ref={ref}
      style={{position: 'relative'}}
      aria-current={current ? 'location' : undefined}
      inert={shouldUseInert}
    />
  )
}

export const SyntaxHighlightedOverlay = React.memo(SyntaxHighlightedOverlayUnmemoed)

function SyntaxHighlightedOverlayUnmemoed({
  linesData,
  colorizedLines,
}: {
  linesData: CodeLineData[]
  colorizedLines: SafeHTMLString[] | null | undefined
}) {
  const hiddenUnicodeShown = isHiddenUnicodeShown()
  const documentHTML = useCodeHTMLDocument(linesData, colorizedLines ?? null, hiddenUnicodeShown)
  const shouldUseInert = useShouldUseInert()

  return <VerifiedHTMLDiv inert={shouldUseInert} html={documentHTML} />
}

function useSyntaxHighlightingStrategy(): HighlightingStrategy {
  const hiddenFromFind = useIsCursorEnabled()
  const isFirefox = useIsFirefox()
  const shouldUseInert = useShouldUseInert()

  if (!hiddenFromFind || shouldUseInert) return 'plain'
  if (isFirefox && !shouldUseInert) return 'separated-characters-chunked'

  return 'data-attribute'
}

export function useCodeHTMLDocument(
  linesData: CodeLineData[],
  colorizedLines: SafeHTMLString[] | null | undefined,
  hiddenUnicodeShown: boolean,
): SafeHTMLString {
  const {isServer: isSSR} = useRuntimeEnvironment()
  // When SSR-rendering large files, batching colorized lines into
  // `.react-no-virtualization-wrapper` boxes lets `content-visibility: auto`
  // skip painting/laying out off-screen lines on first paint. Historically this
  // batching was disabled for SSR; gate re-enabling it behind a flag so it can
  // be released and validated safely.
  const ssrContentVisibilityEnabled = isFeatureEnabled('react_blob_ssr_content_visibility')
  const contentVisibilityWrapped = !isSSR || ssrContentVisibilityEnabled
  return useMemo(() => {
    // The last line that will actually be rendered: lines beyond `linesData.length`
    // are skipped (mapped to null), so the wrapper must close on this index.
    const lastRenderedIndex = Math.min(colorizedLines?.length ?? 0, linesData.length) - 1
    return colorizedLines
      ? /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
        (colorizedLines
          .map((colorizedLine, i) => {
            if (i >= linesData.length) return null
            let divWrapStart = ''
            let divWrapEnd = ''
            //we need to put content visibility=auto on boxes of content rather than on each individual line,
            //that allows the page to do significantly fewer content visibility calculations when doing layout and
            //style updates. Starting with boxes of 60 lines for now
            if (i % numberOfLinesPerContentVisibility === 0 && contentVisibilityWrapped) {
              divWrapStart = '<div class="react-no-virtualization-wrapper">'
            }
            if (
              ((i + 1) % numberOfLinesPerContentVisibility === 0 || i === lastRenderedIndex) &&
              contentVisibilityWrapped
            ) {
              divWrapEnd = '</div>'
            }

            /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
            const lineContent = colorizedLine.length !== 0 ? colorizedLine : ('\n' as SafeHTMLString)
            const processedLineContent = hiddenUnicodeShown
              ? (showHiddenUnicodeCharactersHTML(lineContent) ?? lineContent)
              : lineContent

            return `${divWrapStart}<div id="LC${
              i + 1
            }" class="react-code-text react-code-line-contents-no-virtualization react-file-line html-div ${
              linesData[i]?.codeLineClassName
            }">${processedLineContent}</div>${divWrapEnd}`
          })
          .join('\n') as SafeHTMLString)
      : /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
        ('<div></div>' as SafeHTMLString)
  }, [linesData, colorizedLines, contentVisibilityWrapped, hiddenUnicodeShown])
}
